import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Alert, TextField, Divider, Autocomplete, InputAdornment,
  MenuItem, Chip, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { splitBySize, oversizeMessage } from '../../utils/fileSizeLimit';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { isOrganizationDocType } from '../../utils/docTypeGroups';
import WorkOrderEmployeePicker from './WorkOrderEmployeePicker';
import WorkOrderDocumentRows from './WorkOrderDocumentRows';
import {
  loadEmployeeDocGroups, loadOrgDocGroups, currentGroupOf, fetchPrefilledDoc, prefillKindOf
} from './documentPrefill';
import {
  mergeCustomFields, mergeRequiredDocs, buildServiceRow, customerTotals
} from './workOrderCustomer';

const fontSize = { xs: '0.75rem', sm: '0.875rem', md: '1rem' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(amount || 0);

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return (!s || s.toLowerCase() === 'null') ? '' : s;
};

/**
 * One applicant's services, details, custom fields and documents — the per-customer section of the
 * Create Work Order page, as a controlled component. `customer` is the state, `onChange(updater)`
 * receives a function (prev → next). Uploading a passport/EID etc. OCRs it and fills the empty
 * identity fields; a document whose detected name differs from the entered one is kept but flagged.
 */
const WorkOrderCustomerForm = ({
  customer,
  onChange,
  availableServices = [],
  documentTypes = [],
  showThaklisServices = false,
  employeeTrackingActive = false,
  // Employee selection (as on WO create): pick one of the customer's employees (customerKey: the client's internal
  // org id, or 'walkin') or Quick add one first; the rest of the form stays locked until one is picked.
  employeeMode = false,
  customerKey = null,
  // Employees already on the work order ({ uid, name }), which can't be added again as a new customer.
  takenEmployees = [],
  // The work order's client organization (internal org id) - its company documents pre-fill company rows
  organizationId = null,
  onError
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [markupAmount, setMarkupAmount] = useState('');
  const [serviceTab, setServiceTab] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');
  // Detected names that differ from the customer's — informational only on this single-customer form.
  const [nameMismatches, setNameMismatches] = useState([]);

  const reportError = (msg) => onError && onError(msg);

  // Pre-fill: the picked employee's documents (employee types) and the organization's (company types), current
  // versions. Each row is tried once, so a pre-filled file the user removes stays removed.
  const [empGroups, setEmpGroups] = useState(null);   // null = not loaded for the current employee
  const [orgGroups, setOrgGroups] = useState(null);
  const employeeUid = customer.employee?.uid || null;
  useEffect(() => {
    let live = true;
    setEmpGroups(null);
    if (!employeeUid) return undefined;
    loadEmployeeDocGroups(customerKey, employeeUid).then(g => { if (live) setEmpGroups(g); });
    return () => { live = false; };
  }, [customerKey, employeeUid]);
  useEffect(() => {
    let live = true;
    setOrgGroups(null);
    loadOrgDocGroups(organizationId).then(g => { if (live) setOrgGroups(g); });
    return () => { live = false; };
  }, [organizationId]);
  const update = (fn) => onChange(fn);
  const setField = (field, value) => update(c => ({ ...c, [field]: value }));

  // ---- services ----
  const serviceOptions = showThaklisServices
    ? availableServices.filter(s => (serviceTab === 0 ? s.type === 'INTERNAL' : s.type !== 'INTERNAL'))
    : availableServices.filter(s => s.type === 'INTERNAL');

  const handleAddService = () => {
    if (!selectedService) return;
    const row = buildServiceRow(selectedService, markupAmount);
    update(c => {
      // Seed shared field values from the org defaults on the added service — never overwrite a typed value.
      const nextValues = { ...(c.fieldValues || {}) };
      row.customFields.forEach(f => {
        const def = f.orgDefault;
        const current = nextValues[f.fieldId];
        if (def != null && String(def).trim() !== '' && (current == null || String(current).trim() === '')) {
          nextValues[f.fieldId] = def;
        }
      });
      return { ...c, services: [...c.services, row], fieldValues: nextValues };
    });
    setSelectedService(null);
    setMarkupAmount('');
  };

  const handleRemoveService = (rowId) => update(c => ({ ...c, services: c.services.filter(s => s.id !== rowId) }));

  const handleFieldValue = (fieldId, value) =>
    update(c => ({ ...c, fieldValues: { ...(c.fieldValues || {}), [fieldId]: value } }));

  // ---- documents ----
  // `forced` ({ code, label }) comes from a required-document slot; otherwise the "add another" type is used.
  const handleFileUpload = async (event, forced = null) => {
    const picked = Array.from(event.target.files || []);
    if (event.target) event.target.value = '';
    if (picked.length === 0) return;
    const { ok: files, tooBig } = splitBySize(picked);
    if (tooBig.length) reportError(tooBig.map(oversizeMessage).join(' '));
    if (files.length === 0) return;

    const docTypeCode = forced?.code || selectedDocType;
    if (!docTypeCode) { reportError('Please select a document type'); return; }
    const docType = documentTypes.find(d => (d.code || d.id) === docTypeCode);
    const baseId = Date.now();
    // A company document is not read by Thaklis' person OCR (it has no applicant, and a "licence" is taken for a
    // driving licence); the backend reads it when it is stored in the customer's company documents.
    const companyDoc = isOrganizationDocType(documentTypes, docTypeCode);
    const staged = files.map((file, i) => ({
      id: baseId + i,
      file,
      fileName: file.name,
      fileSize: file.size,
      documentType: docTypeCode,
      documentTypeName: forced?.label || docType?.label || docType?.name || docTypeCode,
      extracting: !companyDoc,
      documentNumber: null,
      issueDate: null,
      expiryDate: null
    }));
    update(c => ({ ...c, documents: [...c.documents, ...staged] }));
    if (!forced) setSelectedDocType('');
    if (companyDoc) return;

    // Best-effort OCR per file: fill empty identity fields and keep each doc's own number/issue/expiry.
    await Promise.all(staged.map(async (doc) => {
      try {
        const response = await ApiService.extractDocument(doc.file, 'GENERAL', doc.documentType);
        const extracted = response?.data?.data || {};
        const failure = response?.data?.success === false
          ? (response.data.error || response.data.message)
          : (extracted._extractionSkipped
            ? (extracted._extractionSkippedReason || 'Automatic field extraction was skipped — enter the fields manually.')
            : null);
        if (failure) reportError(failure);

        const fullName = clean(extracted.fullName);
        const passportNumber = clean(extracted.passportNumber);
        const emiratesIdNumber = clean(extracted.emiratesIdNumber);
        const phoneNumber = clean(extracted.phoneNumber);
        const email = clean(extracted.email);
        const meta = {
          extracting: false,
          documentNumber: resolveExtractedDocNumber(extracted, doc.documentType),
          issueDate: clean(extracted.issueDate),
          expiryDate: clean(extracted.expiryDate)
        };

        update(c => {
          const existingName = c.name?.trim() || '';
          const docs = c.documents.map(d => (d.id === doc.id ? { ...d, ...meta } : d));
          if (existingName && fullName && existingName.toLowerCase() !== fullName.toLowerCase()) {
            setNameMismatches(prev => (prev.some(m => m.docId === doc.id)
              ? prev
              : [...prev, { docId: doc.id, fileName: doc.fileName, detected: fullName, existing: existingName }]));
            return { ...c, documents: docs };
          }
          return {
            ...c,
            name: existingName ? c.name : (fullName || c.name),
            passportNumber: c.passportNumber?.trim() ? c.passportNumber : (passportNumber || c.passportNumber),
            emiratesId: c.emiratesId?.trim() ? c.emiratesId : (emiratesIdNumber || c.emiratesId),
            phone: c.phone?.trim() ? c.phone : (phoneNumber || c.phone),
            email: c.email?.trim() ? c.email : (email || c.email),
            documents: docs
          };
        });
      } catch (err) {
        console.error('Document extraction failed:', err);
        reportError('Document extraction failed — enter the fields manually.');
        update(c => ({ ...c, documents: c.documents.map(d => (d.id === doc.id ? { ...d, extracting: false } : d)) }));
      }
    }));
  };

  useEffect(() => {
    if ((employeeMode && !employeeUid) || orgGroups === null || (employeeUid && empGroups === null)) return;
    const tried = new Set(customer._prefillTried || []);
    const jobs = docRows
      .filter(row => row.code && !tried.has(row.code) && !customer.documents.some(d => d.documentType === row.code))
      .map(row => {
        const kind = prefillKindOf(documentTypes, row.code);
        return { row, kind, group: currentGroupOf(kind === 'org' ? orgGroups : (empGroups || []), row.code) };
      });
    if (!jobs.length) return;
    update(c => ({ ...c, _prefillTried: [...(c._prefillTried || []), ...jobs.map(j => j.row.code)] }));
    jobs.filter(j => j.group).forEach(j => {
      fetchPrefilledDoc({ kind: j.kind, group: j.group, code: j.row.code, label: j.row.label, customerKey, orgId: organizationId })
        .then(doc => {
          if (!doc) return;
          update(c => (c.documents.some(d => d.documentType === j.row.code) || (j.kind === 'employee' && c.employee?.uid !== employeeUid)
            ? c : { ...c, documents: [...c.documents, doc] }));
        })
        .catch(err => {
          console.error(`[prefill] ${j.row.code}: the file could not be fetched`, err);
          reportError(`The ${j.row.label || j.row.code} on file could not be attached - upload it on the row.`);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, empGroups, orgGroups, employeeMode, employeeUid, documentTypes]);

  const handleRemoveDocument = (docId) => {
    update(c => ({ ...c, documents: c.documents.filter(d => d.id !== docId) }));
    setNameMismatches(prev => prev.filter(m => m.docId !== docId));
  };

  // ---- derived ----
  const totals = customerTotals(customer);
  const mergedFields = mergeCustomFields(customer);
  const mergedDocs = mergeRequiredDocs(customer);
  const mergedDocCodes = new Set(mergedDocs.map(d => d.code));
  // The rows: the services' required documents, then the types added from "Add document" (optional).
  const docRows = [
    ...mergedDocs,
    ...(customer.extraDocTypes || []).filter(code => !mergedDocCodes.has(code)).map(code => {
      const dt = documentTypes.find(d => (d.code || d.id) === code);
      return { code, label: dt?.label || dt?.name || code, required: false };
    }),
  ];
  const addExtraDocType = (code) => {
    if (!code) return;
    update(c => ((c.extraDocTypes || []).includes(code) ? c : { ...c, extraDocTypes: [...(c.extraDocTypes || []), code] }));
  };

  // Picking an employee fills the applicant; changing it clears what the previous one filled.
  const selectEmployee = (emp) => {
    const taken = takenEmployees.find(t => t.uid && t.uid === emp?.uid);
    if (taken) {
      reportError(`${taken.name || emp.name || 'This employee'} is already a customer on this work order - add services to them instead.`);
      return;
    }
    applyEmployee(emp);
  };
  const applyEmployee = (emp) => update(c => ({
    ...c,
    documents: c.documents.filter(d => !d._fromEmployee),
    _prefillTried: (c._prefillTried || []).filter(code => isOrganizationDocType(documentTypes, code)),
    employee: emp,
    name: emp.name || c.name,
    passportNumber: emp.passportNumber || '',
    emiratesId: emp.emiratesId || ''
  }));
  const clearEmployee = () => update(c => ({
    ...c, employee: null, name: '', passportNumber: '', emiratesId: '',
    documents: c.documents.filter(d => !d._fromEmployee),
    _prefillTried: (c._prefillTried || []).filter(code => isOrganizationDocType(documentTypes, code)),
  }));
  const locked = employeeMode && !customer.employee;

  return (
    <Box>
      {employeeMode && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1, color: 'secondary.main' }}>Applicant</Typography>
          <WorkOrderEmployeePicker
            customer={customerKey}
            employee={customer.employee}
            onSelect={selectEmployee}
            onClear={clearEmployee}
            onError={reportError}
          />
        </Box>
      )}

      {/* Everything below opens once the employee is picked (employee selection) */}
      <Box sx={locked ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : undefined} aria-disabled={locked || undefined}>
      {/* Services */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
        Services for {customer.name || 'this Customer'}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
        {showThaklisServices && (
          <>
            <Tabs value={serviceTab} onChange={(e, v) => { setServiceTab(v); setSelectedService(null); }} sx={{ mb: 1, minHeight: 36 }}>
              <Tab label="Internal Services" sx={{ minHeight: 36, py: 0 }} />
              <Tab label="Thaklis Services" sx={{ minHeight: 36, py: 0 }} />
            </Tabs>
            {serviceTab === 1 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These services are provided by Thaklis — link each one to its ticket from the task once the customer is added.
              </Alert>
            )}
          </>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={serviceOptions}
              getOptionLabel={(o) => o.name}
              value={selectedService}
              onChange={(e, v) => setSelectedService(v)}
              size="small"
              renderInput={(params) => <TextField {...params} label="Select Service" placeholder="Choose a service..." />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Markup (AED)" type="number"
              value={markupAmount} onChange={(e) => setMarkupAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            {selectedService && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                  {formatCurrency((selectedService.service_charge || 0) + (selectedService.govt_charge || 0) + (parseFloat(markupAmount) || 0))}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={handleAddService} disabled={!selectedService}>
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {customer.services.length === 0 ? (
        <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>
          No services added yet. Select a service above to add.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.8rem' } }}>
                <TableCell>Service</TableCell>
                <TableCell align="right">Service Chg</TableCell>
                <TableCell align="right">Govt Chg</TableCell>
                <TableCell align="right">Markup</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center" width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {customer.services.map((item) => (
                <TableRow hover key={item.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{item.service_name}</Typography>
                    <Chip label={item.service_type} size="small" sx={{ fontSize: '0.65rem', height: 18, mt: 0.5 }}
                      color={item.service_type === 'THAKLIS' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.service_charge)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.govt_charge)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', color: item.markup_amount > 0 ? 'success.main' : 'text.disabled' }}>
                    {item.markup_amount > 0 ? formatCurrency(item.markup_amount) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(item.tax_value)}</Typography>
                    {item.tax_on_markup > 0 && (
                      <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>(+{formatCurrency(item.tax_on_markup)})</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatCurrency(item.line_total)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="error" onClick={() => handleRemoveService(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>Customer Subtotal:</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>{formatCurrency(totals.grandTotal)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Customer information */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>Customer Information</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Full Name" required value={customer.name}
            onChange={(e) => setField('name', e.target.value)} placeholder="Enter full name" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Passport Number" value={customer.passportNumber}
            onChange={(e) => setField('passportNumber', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Emirates ID" value={customer.emiratesId}
            onChange={(e) => setField('emiratesId', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Phone Number" value={customer.phone}
            onChange={(e) => setField('phone', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Email" type="email" value={customer.email}
            onChange={(e) => setField('email', e.target.value)} placeholder="Optional" />
        </Grid>
      </Grid>
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 1 }}>
        Tip: upload the passport or Emirates ID below and the empty fields are filled in automatically.
      </Typography>
      {employeeTrackingActive && !employeeMode && !customer.passportNumber?.trim() && !customer.emiratesId?.trim() && (
        <Alert severity="warning" sx={{ mt: 2, fontSize: fontSize.xs }}>
          No unique identifier for this applicant. Add a Passport Number or Emirates ID — without one, the employee and
          their documents can't be tracked, so you won't get expiry reminders for their documents.
        </Alert>
      )}
      {nameMismatches.map(m => (
        <Alert key={m.docId} severity="info" sx={{ mt: 1.5, fontSize: fontSize.xs }}
          onClose={() => setNameMismatches(prev => prev.filter(x => x.docId !== m.docId))}>
          {m.fileName} looks like it belongs to <strong>{m.detected}</strong>, not {m.existing}. It stays with this
          customer — remove it if it was added by mistake.
        </Alert>
      ))}

      {/* Custom fields */}
      {mergedFields.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>Service Information</Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
            Details needed for this customer&apos;s services. Shared fields are entered once.
          </Typography>
          <Grid container spacing={2}>
            {mergedFields.map((field) => {
              const val = customer.fieldValues?.[field.fieldId] || '';
              const label = field.label + (field.required ? ' *' : '');
              return (
                <Grid item xs={12} sm={field.fieldType === 'TEXTAREA' ? 12 : 4} key={field.fieldId}>
                  {field.fieldType === 'SELECT' ? (
                    <TextField select fullWidth size="small" label={label} value={val}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {(field.options || []).map((opt, oi) => (
                        <MenuItem key={oi} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                          {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : field.fieldType === 'DATE' ? (
                    <TextField fullWidth size="small" type="date" label={label} value={val}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)} InputLabelProps={{ shrink: true }} />
                  ) : (
                    <TextField fullWidth size="small" label={label} value={val}
                      type={field.fieldType === 'NUMBER' ? 'number' : 'text'}
                      multiline={field.fieldType === 'TEXTAREA'} rows={field.fieldType === 'TEXTAREA' ? 2 : undefined}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)} />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Documents */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
        Documents for {customer.name || 'this Customer'}
      </Typography>
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1 }}>
        {mergedDocs.length > 0
          ? 'Attach the documents these services need. Required documents must be uploaded before the customer can be added.'
          : 'Add the documents for this customer.'}
        {' '}Documents already on file are filled in.
      </Typography>
      <WorkOrderDocumentRows
        rows={docRows}
        documents={customer.documents}
        documentTypes={documentTypes}
        onAddType={addExtraDocType}
        onFiles={(files, row) => handleFileUpload({ target: { files, value: '' } }, row)}
        onRemove={handleRemoveDocument}
        captureName={customer.name}
      />
      </Box>
    </Box>
  );
};

export default WorkOrderCustomerForm;
